import React, {
    Dispatch, SetStateAction, createContext, useContext, useEffect, useState,
} from 'react';
import { PageSpecificKeys } from 'helpers/localStorage';
import { IUsesExpandedCategories, IUsesSelectedCategories } from 'views/ProFormaTable/types';
import { USES_EXPANDED_CATEGORIES } from 'views/ProFormaTable/constants';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';

export interface IUsesTableContext {
    selectedCategories: IUsesSelectedCategories;
    setSelectedCategories: Dispatch<SetStateAction<IUsesSelectedCategories>>;
    expandedCategories: IUsesExpandedCategories;
    setExpandedCategories: Dispatch<SetStateAction<IUsesExpandedCategories>>;
    updateSelectedCategories: (highLevelId: number | null, subcategoryIds: number[] | null, budgetClassificationIds: number[]) => void;
    updateExpandedCategories: (highLevelId: number | null, subcategoryId: number | null) => void;
    deselectAllCategories: () => void;
    selectAllCategories: (updatedCategories: IUsesSelectedCategories) => void;
    hideAllCategories: () => void;
    showAllCategories: (updatedCategories: IUsesExpandedCategories) => void;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    isProFormaReadOnly: boolean;
}

export const defaultValues: IUsesTableContext = {
    selectedCategories: { highLevels: [], subcategories: [], budgetClassifications: [] },
    setSelectedCategories: () => { },
    expandedCategories: { highLevels: [], subcategories: [] },
    setExpandedCategories: () => { },
    updateSelectedCategories: () => { },
    updateExpandedCategories: () => { },
    deselectAllCategories: () => { },
    selectAllCategories: () => { },
    hideAllCategories: () => { },
    showAllCategories: () => { },
    navigateToSection: () => { },
    isProFormaReadOnly: false,
};

const UsesTableContext = createContext<IUsesTableContext>(defaultValues);

interface IUsesTableProviderProps {
    children: React.ReactNode;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    isProFormaReadOnly: boolean;
    initialExpandedState?: IUsesExpandedCategories;
}
const UsesTableProvider = ({
    children, navigateToSection, isProFormaReadOnly, initialExpandedState,
}: IUsesTableProviderProps) => {
    const { proForma } = useProFormaSocketContext();
    const proFormaPageLocalStorage = JSON.parse(localStorage.getItem(PageSpecificKeys.ProFormaPage) ?? '{}');

    const [selectedCategories, setSelectedCategories] = useState<IUsesSelectedCategories>({
        highLevels: [],
        subcategories: [],
        budgetClassifications: [],
    });
    const [expandedCategories, setExpandedCategories] = useState<IUsesExpandedCategories>(
        initialExpandedState || { highLevels: [], subcategories: [] },
    );

    const updateSelectedCategories = (highLevelId: number | null, subcategoryIds: number[] | null, budgetClassificationIds: number[]) => {
        /**
         * selecting any level also selects its children
         * deselecting any level also deselects it's children
         */
        const newCategories = { ...selectedCategories };

        if (highLevelId) {
            if (!newCategories.highLevels.includes(highLevelId)) {
                newCategories.highLevels.push(highLevelId);
                if (subcategoryIds) {
                    newCategories.subcategories = [...new Set([...newCategories.subcategories, ...subcategoryIds])];
                }
                if (budgetClassificationIds) {
                    newCategories.budgetClassifications = [...new Set([...newCategories.budgetClassifications, ...budgetClassificationIds])];
                }
            } else {
                newCategories.highLevels = newCategories.highLevels.filter(id => id !== highLevelId);
                if (subcategoryIds) {
                    newCategories.subcategories = newCategories.subcategories.filter(id => !subcategoryIds.includes(id));
                }
                if (budgetClassificationIds) {
                    newCategories.budgetClassifications = newCategories.budgetClassifications.filter(id => !budgetClassificationIds.includes(id));
                }
            }
        } else if (subcategoryIds && subcategoryIds.length > 0) {
            subcategoryIds.forEach(subcategoryId => {
                if (!newCategories.subcategories.includes(subcategoryId)) {
                    newCategories.subcategories.push(subcategoryId);
                    if (budgetClassificationIds) {
                        newCategories.budgetClassifications = [...new Set([...newCategories.budgetClassifications, ...budgetClassificationIds])];
                    }
                } else {
                    newCategories.subcategories = newCategories.subcategories.filter(id => id !== subcategoryId);
                    if (budgetClassificationIds) {
                        newCategories.budgetClassifications = newCategories.budgetClassifications.filter(id => !budgetClassificationIds.includes(id));
                    }
                }
            });
        } else if (budgetClassificationIds && budgetClassificationIds.length > 0) {
            budgetClassificationIds.forEach(budgetClassificationId => {
                if (!newCategories.budgetClassifications.includes(budgetClassificationId)) {
                    newCategories.budgetClassifications.push(budgetClassificationId);
                } else {
                    newCategories.budgetClassifications = newCategories.budgetClassifications.filter(id => id !== budgetClassificationId);
                }
            });
        }

        setSelectedCategories(newCategories);
    };

    const deselectAllCategories = () => {
        setSelectedCategories({ highLevels: [], subcategories: [], budgetClassifications: [] });
    };

    const selectAllCategories = (updatedCategories: IUsesSelectedCategories) => {
        setSelectedCategories(updatedCategories);
    };

    const updateExpandedCategories = (highLevelId: number | null, subcategoryId: number | null) => {
        if (highLevelId) {
            setExpandedCategories(prev => ({
                ...prev,
                highLevels: prev.highLevels.includes(highLevelId) ? prev.highLevels.filter(id => id !== highLevelId) : [...prev.highLevels, highLevelId],
            }));
        }

        if (subcategoryId) {
            setExpandedCategories(prev => ({
                ...prev,
                subcategories: prev.subcategories.includes(subcategoryId)
                    ? prev.subcategories.filter(id => id !== subcategoryId)
                    : [...prev.subcategories, subcategoryId],
            }));
        }
    };

    const hideAllCategories = () => {
        setExpandedCategories({ highLevels: [], subcategories: [] });
    };

    const showAllCategories = (updatedCategories: IUsesExpandedCategories) => {
        setExpandedCategories(updatedCategories);
    };

    useEffect(() => {
        if (!proForma) return;

        const proFormaPageLocalStorage = JSON.parse(localStorage.getItem(PageSpecificKeys.ProFormaPage) ?? '{}');
        const updatedProFormaPageLocalStorage = {
            ...proFormaPageLocalStorage,
            [proForma.id]: {
                ...proFormaPageLocalStorage[proForma.id],
                [USES_EXPANDED_CATEGORIES]: expandedCategories,
            },
        };

        localStorage.setItem(PageSpecificKeys.ProFormaPage, JSON.stringify(updatedProFormaPageLocalStorage));
    }, [expandedCategories, proForma]);

    useEffect(() => {
        if (proFormaPageLocalStorage) {
            const currentProFormaLocalStorage = proForma && proForma.id in proFormaPageLocalStorage
                ? proFormaPageLocalStorage[proForma.id]
                : {};

            if (expandedCategories.highLevels.length === 0 && expandedCategories.subcategories.length === 0) {
                const savedExpandedCategories = USES_EXPANDED_CATEGORIES in currentProFormaLocalStorage
                    ? currentProFormaLocalStorage[USES_EXPANDED_CATEGORIES]
                    : { highLevels: [], subcategories: [] };
                setExpandedCategories(savedExpandedCategories as IUsesExpandedCategories);
            }
        }
    }, []);

    return (
        <UsesTableContext.Provider
            value={{
                selectedCategories,
                setSelectedCategories,
                expandedCategories,
                setExpandedCategories,
                updateSelectedCategories,
                updateExpandedCategories,
                deselectAllCategories,
                selectAllCategories,
                hideAllCategories,
                showAllCategories,
                navigateToSection,
                isProFormaReadOnly,
            }}
        >
            {children}
        </UsesTableContext.Provider>
    );
};

const useUsesTableContext = () => useContext(UsesTableContext);

export { UsesTableProvider, useUsesTableContext };
